<script setup lang="ts">
import { isPhoneValid, isEmailValid } from 'src/composables/utils/validators';

const props = defineProps<{
  type: 'phone' | 'email';
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: 'submit', value: string): void;
  (e: 'skip'): void;
}>();

const { t } = useI18n();

const inputValue = ref('');
const showError = ref(false);

const placeholder = computed(() =>
  props.type === 'phone' ? '+7 999 123 45 67' : 'name@mail.ru',
);

const inputType = computed(() => (props.type === 'phone' ? 'tel' : 'email'));

const handleSubmit = () => {
  const value = inputValue.value.trim();
  if (!value) return;

  const isValid = props.type === 'phone'
    ? isPhoneValid(value)
    : isEmailValid(value);

  if (!isValid) {
    showError.value = true;
    return;
  }

  showError.value = false;
  emit('submit', value);
  inputValue.value = '';
};

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    handleSubmit();
  }
};

// Сбрасываем ошибку при изменении ввода
watch(inputValue, () => {
  showError.value = false;
});
</script>

<template lang="pug">
.contact-input
  .contact-input__field
    input.contact-input__input(
      v-model="inputValue"
      :type="inputType"
      :placeholder="placeholder"
      :disabled="disabled"
      @keydown="handleKeydown"
    )
    button.contact-input__submit(
      :disabled="disabled || !inputValue.trim()"
      @click="handleSubmit"
    )
      svg(width="16" height="16" viewBox="0 0 18 18" fill="none")
        path(d="M2 9l14-7-7 14v-7H2z" fill="currentColor")

  .contact-input__error(v-if="showError")
    span {{ type === 'phone' ? t('HYBRID_CHAT.ERROR.INVALID_PHONE') : t('HYBRID_CHAT.ERROR.INVALID_EMAIL') }}

  button.contact-input__skip(
    type="button"
    :disabled="disabled"
    @click="emit('skip')"
  ) {{ t('HYBRID_CHAT.CHIP.SKIP') }}
</template>

<style lang="sass">
.contact-input
  display: flex
  flex-direction: column
  gap: 6px

  &__field
    display: flex
    gap: 8px
    align-items: center

  &__input
    flex: 1
    background: rgba(255, 255, 255, 0.06)
    border: 1px solid rgba(255, 255, 255, 0.15)
    border-radius: 12px
    padding: 10px 14px
    color: white
    font-size: 14px
    outline: none
    font-family: inherit
    transition: border-color 0.2s

    &::placeholder
      color: rgba(255, 255, 255, 0.3)

    &:focus
      border-color: rgba(255, 255, 255, 0.3)

    &:disabled
      opacity: 0.5

  &__submit
    width: 36px
    height: 36px
    border-radius: 50%
    background: white
    color: #1a1a1a
    border: none
    cursor: pointer
    display: flex
    align-items: center
    justify-content: center
    flex-shrink: 0
    transition: opacity 0.2s, transform 0.2s

    &:disabled
      opacity: 0.3
      cursor: not-allowed

    &:not(:disabled):hover
      transform: scale(1.05)

  &__error
    font-size: 12px
    color: #f87171
    padding: 0 4px

  &__skip
    align-self: flex-start
    background: none
    border: none
    padding: 2px 4px
    color: rgba(255, 255, 255, 0.4)
    font-size: 12px
    font-family: inherit
    cursor: pointer
    transition: color 0.2s

    &:hover:not(:disabled)
      color: rgba(255, 255, 255, 0.8)

    &:disabled
      opacity: 0.4
      cursor: not-allowed
</style>
